import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  IconAlertTriangle,
  IconArrowsCross,
  IconBook2,
  IconBookmark,
  IconChecks,
  IconFiles,
  IconFolder,
  IconFolderOpen,
  IconGavel,
  IconHighlight,
  IconHistory,
  IconListDetails,
  IconMessage2Question,
  IconNote,
  IconPencil,
  IconQuote,
  IconRoute,
  IconScale,
  IconSparkles,
  IconTimeline,
  IconUserCircle,
  IconUserHeart,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import Breadcrumb from '../components/ui/Breadcrumb';
import Btn from '../components/ui/Btn';
import StatusBadge from '../components/ui/StatusBadge';
import DossierAccessPanel from '../components/DossierAccessPanel';
import AiResearchTab from '../components/AiResearchTab';
import AiToolCard from '../components/AiToolCard';
import BriefingsTab from '../components/BriefingsTab';
import MemoInterrogatoireTab from '../components/MemoInterrogatoireTab';
// AlertesProceduralesTab retiré 2026-05-13 (remplacé par Synthèse IA / Droit)
import AnalyseIaButton from '../components/AnalyseIaButton';
import AnnotationsCitationsTab from '../components/AnnotationsCitationsTab';
import GDriveSyncButton from '../components/GDriveSyncButton';
import AutoSortButton from '../components/AutoSortButton';
import ActesGeneratorTab from '../components/ActesGeneratorTab';
import BulkExportModal from '../components/BulkExportModal';
import ConclusionsStorageTab from '../components/ConclusionsStorageTab';
import OtherDocumentsTab from '../components/OtherDocumentsTab';
import ContradictionsTab from '../components/ContradictionsTab';
import EtatProcedureTab from '../components/EtatProcedureTab';
import FileDropZone from '../components/FileDropZone';
import InventaireTab from '../components/InventaireTab';
import MigratePieceDialog from '../components/MigratePieceDialog';
import CoherenceTab from '../components/CoherenceTab';
import ConclusionsTab from '../components/ConclusionsTab';
import JournalTab from '../components/JournalTab';
import MiniDossierTab from '../components/MiniDossierTab';
import NoteGeneraleTab from '../components/NoteGeneraleTab';
import PartiesTab from '../components/PartiesTab';
import PiecesAdversesTab from '../components/PiecesAdversesTab';
import PiecesClientTab from '../components/PiecesClientTab';
// RechercheTab retiré 2026-05-13 (remplacé par Synthèse IA)
import TimelineTab from '../components/TimelineTab';
import { ApiError, api } from '../lib/api';
import { confirmAndDeletePdf } from '../lib/pdfActions';
import {
  type InventorySourcePreference,
  sortPieces,
  sortPiecesByInventory,
} from '../lib/sortPieces';
import { formatDossierRef, type PaymentMode } from '../lib/types';
import { toast } from '../stores/toaster';
import {
  AiCostTotal,
  Annotation,
  Bookmark,
  Dossier,
  ExportJobStatus,
  InventaireEntry,
  Partie,
  PdfMetadata,
  TimelineEvent,
} from '../lib/types';
import { useAuthStore } from '../stores/auth';
import { JURIDICTIONS_BE } from '../lib/juridictionsBE';

/**
 * Vue dossier — refonte navigation (ADR-025, 2026-05-11) : 5 onglets
 * top-level (Lecture / Dossier / Analyse / Outils IA / Journal) avec
 * sous-onglets pour Dossier, Analyse et Outils IA. URL-based via
 * React Router (`/dossiers/:id/<top>/<sub?>`).
 *
 * « Lecture » (renommage de l'ex « Analyse ») reste l'onglet par
 * défaut — vue-liste des pièces triées par carton + statut couleur
 * (untouched / in_progress / done). Le visualiseur PDF.js + outils
 * de surlignage vit sur la route séparée `/pieces/:pdfId` (cf
 * PdfEditorPage).
 */

type TopTab = 'lecture' | 'dossier' | 'etat' | 'analyse' | 'outils-ia' | 'actes' | 'journal';
type SubTab =
  // dossier
  | 'pieces'
  | 'inventaire'
  | 'parties'
  | 'pieces-client'
  | 'pieces-adverses'
  | 'conclusions'
  | 'autres'
  // analyse
  | 'annotations-citations'
  | 'mini-dossier'
  | 'timeline'
  | 'note-generale'
  // outils-ia
  | 'synthese'
  | 'charges'
  | 'decharges'
  | 'droit'
  | 'coherence'
  | 'contradictions'
  // actes
  | 'rediger-conclusions'
  | 'actes-procedure'
  | 'briefing-client'
  | 'memo-interrogatoire';

// Icônes Tabler outline (jamais filled) — taille 16px dans la nav.
type IconCmp = typeof IconBook2 | typeof IconFolder;

const TOP_TABS: { id: TopTab; label: string; Icon: IconCmp }[] = [
  { id: 'lecture', label: 'Lecture', Icon: IconBook2 },
  { id: 'dossier', label: 'Dossier', Icon: IconFolder },
  { id: 'etat', label: 'État', Icon: IconRoute },
  { id: 'analyse', label: 'Analyse', Icon: IconHighlight },
  { id: 'outils-ia', label: 'Outils IA', Icon: IconSparkles },
  { id: 'actes', label: 'Actes', Icon: IconScale },
  { id: 'journal', label: 'Journal', Icon: IconHistory },
];

const SUB_TABS: Record<
  TopTab,
  { id: SubTab; label: string; labelShort?: string; Icon: IconCmp }[]
> = {
  lecture: [],
  dossier: [
    { id: 'pieces', label: 'Pièces', Icon: IconFiles },
    { id: 'inventaire', label: 'Inventaire', Icon: IconListDetails },
    { id: 'parties', label: 'Parties', Icon: IconUsers },
    { id: 'pieces-client', label: 'Pièces client', Icon: IconUserCircle },
    { id: 'pieces-adverses', label: 'Conclusions et pièces adverses', Icon: IconUsersGroup },
    { id: 'conclusions', label: 'Nos conclusions', Icon: IconPencil },
    { id: 'autres', label: 'Autres', Icon: IconFiles },
  ],
  analyse: [
    {
      id: 'annotations-citations',
      label: 'Annotations & citations',
      labelShort: 'Annot. & citations',
      Icon: IconQuote,
    },
    { id: 'mini-dossier', label: 'Mini dossier', Icon: IconFolderOpen },
    { id: 'timeline', label: 'Timeline', Icon: IconTimeline },
    { id: 'note-generale', label: 'Note générale', Icon: IconNote },
  ],
  'outils-ia': [
    { id: 'synthese', label: 'Synthèse', Icon: IconSparkles },
    { id: 'charges', label: 'Éléments à charge', Icon: IconAlertTriangle },
    { id: 'decharges', label: 'Éléments à décharge', Icon: IconBookmark },
    { id: 'droit', label: 'Droit', Icon: IconGavel },
    { id: 'coherence', label: 'Cohérence', Icon: IconChecks },
    { id: 'contradictions', label: 'Contradictions', Icon: IconArrowsCross },
  ],
  etat: [],
  actes: [
    { id: 'rediger-conclusions', label: 'Génération (drafts IA)', Icon: IconPencil },
    { id: 'actes-procedure', label: 'Actes de procédure', Icon: IconScale },
    { id: 'briefing-client', label: 'Briefing client', Icon: IconUserHeart },
    { id: 'memo-interrogatoire', label: 'Mémo interrogatoire', Icon: IconMessage2Question },
  ],
  journal: [],
};

/** Premier sous-onglet sélectionné quand on clique sur un top-tab.
 *  `null` = pas de sous-onglet (feuille ou vue d'accueil). */
const DEFAULT_SUB: Record<TopTab, SubTab | null> = {
  lecture: null,
  dossier: 'pieces',
  etat: null,
  analyse: 'annotations-citations',
  'outils-ia': null, // vue d'accueil = grille AiToolCard
  actes: 'rediger-conclusions',
  journal: null,
};

function parseTabFromPath(
  pathname: string,
  dossierId: string
): {
  top: TopTab;
  sub: SubTab | null;
} {
  const stripped = pathname.replace(`/dossiers/${dossierId}`, '').replace(/^\/+|\/+$/g, '');
  const [t, s] = stripped.split('/');
  const top = TOP_TABS.find((x) => x.id === t)?.id ?? 'lecture';
  const subs = SUB_TABS[top];
  const sub = subs.find((x) => x.id === s)?.id ?? null;
  return { top, sub };
}

function topTabBadge(
  topId: TopTab,
  counts: { pdfs: number; annotations: number; timeline: number; bookmarks: number }
): number | null {
  if (topId === 'dossier' && counts.pdfs > 0) return counts.pdfs;
  return null;
}

function subTabBadge(
  subId: SubTab,
  counts: { pdfs: number; annotations: number; timeline: number; bookmarks: number }
): number | null {
  if (subId === 'pieces' && counts.pdfs > 0) return counts.pdfs;
  if (subId === 'annotations-citations' && counts.annotations > 0) return counts.annotations;
  if (subId === 'mini-dossier' && counts.bookmarks > 0) return counts.bookmarks;
  if (subId === 'timeline' && counts.timeline > 0) return counts.timeline;
  return null;
}

/** localStorage key pour le dernier PDF consulté par dossier. */
const LAST_PDF_KEY = (dossierId: string) => `adr.lastPdf.${dossierId}`;

/**
 * « Carton IX · farde B · pièce 14 » — never abbreviated (charter §2).
 * Depuis ADR-046, la cotation est dérivée de l'``InventaireEntry`` liée
 * au PDF (champ ``inventaire_entry_id``). Si aucune entry n'est liée
 * ou que la cotation n'est pas renseignée, on affiche le placeholder.
 */
function cotationLongPdf(entry: InventaireEntry | undefined): string {
  if (!entry) return '— non coté';
  const parts: string[] = [];
  if (entry.carton) parts.push(`Carton ${entry.carton}`);
  if (entry.sous_farde) parts.push(`farde ${entry.sous_farde}`);
  if (entry.piece) parts.push(`pièce ${entry.piece}`);
  return parts.length > 0 ? parts.join(' · ') : '— non coté';
}

type UploadStatus = {
  name: string;
  size: number;
  state: 'pending' | 'uploading' | 'done' | 'failed';
  detail?: string;
};

export default function DossierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const userRole = useAuthStore((s) => s.user?.role ?? '');
  const canDeleteDossier = userRole === 'admin_cabinet' || userRole === 'super_admin';

  const onDeleteDossier = async () => {
    if (!dossier) return;
    if (
      !confirm(
        `Mettre le dossier ${dossier.ref_cabinet} à la corbeille ?\n\n` +
          `Il sera masqué et restaurable pendant 30 jours, puis supprimé ` +
          `définitivement. Une archive ZIP est générée automatiquement.`
      )
    )
      return;
    try {
      await api.delete(`/dossiers/${id}`);
      toast.success('Dossier mis à la corbeille');
      navigate('/dossiers');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [pdfs, setPdfs] = useState<PdfMetadata[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [parties, setParties] = useState<Partie[]>([]);
  // ADR-025 : compteur Parties retiré de l'UI ; state `parties` conservé
  // pour alimenter encore certains tabs (Annotations & citations etc.).
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const timelineCount = timelineEvents.length;
  const [inventaireEntries, setInventaireEntries] = useState<InventaireEntry[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [aiCost, setAiCost] = useState<AiCostTotal | null>(null);
  const [indexHealth, setIndexHealth] = useState<{
    ratio: number;
    ocr_pending: number;
    fully_indexed: boolean;
    indexed_pages: number;
    text_pages: number;
  } | null>(null);
  const [showAiCostDetails, setShowAiCostDetails] = useState(false);
  const [exportingBookmarks, setExportingBookmarks] = useState(false);
  const [exportingCitations, setExportingCitations] = useState(false);
  const [bulkExportOpen, setBulkExportOpen] = useState(false);
  // Sprint 3+ : migration d'une pièce DR → autre bucket
  const [migratePdf, setMigratePdf] = useState<{ id: string; title: string } | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  // Sprint E (ADR-024) — préparation audience
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [generatingMemo, setGeneratingMemo] = useState(false);
  const [generatingBriefing, setGeneratingBriefing] = useState(false);
  const [briefingOptions, setBriefingOptions] = useState({
    juridiction: '',
    date_audience: '',
    posture: '',
    remarques_avocat: '',
  });
  // Sprint 2 §9.1 — édition des métadonnées du dossier (titre, parties, refs, BAJ/Payant…).
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [metaDraft, setMetaDraft] = useState<{
    ref_cabinet: string;
    titre: string;
    client_name: string;
    ref_parquet: string;
    ref_forlex: string;
    juridiction: string;
    numero_role: string;
    payment_mode: PaymentMode | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const { top: activeTop, sub: activeSub } = parseTabFromPath(location.pathname, id ?? '');
  const navigateToTop = (t: TopTab) => {
    const defaultSub = DEFAULT_SUB[t];
    navigate(`/dossiers/${id}/${t}${defaultSub ? `/${defaultSub}` : ''}`);
  };
  const navigateToSub = (t: TopTab, s: SubTab) => {
    navigate(`/dossiers/${id}/${t}/${s}`);
  };

  // URL clean : si on est sur `/dossiers/:id` sans suffixe, redirige
  // vers la vue Lecture pour qu'un partage de lien soit lisible.
  useEffect(() => {
    if (!id) return;
    const stripped = location.pathname.replace(`/dossiers/${id}`, '').replace(/^\/+|\/+$/g, '');
    if (!stripped) {
      navigate(`/dossiers/${id}/lecture`, { replace: true });
    }
  }, [id, location.pathname, navigate]);
  const [uploads, setUploads] = useState<UploadStatus[]>([]);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [exportOrg, setExportOrg] = useState<'by_category' | 'by_document' | 'by_party'>(
    'by_category'
  );
  // ADR-066 raffinement : sélecteur multi-source. ``null`` = exporter le
  // dossier entier (comportement par défaut). Sinon, on envoie les sets
  // explicites comme pdf_file_ids / piece_client_ids dans le body.
  const [piecesClient, setPiecesClient] = useState<
    { id: string; titre: string; date_piece: string | null }[]
  >([]);
  const [exportSelector, setExportSelector] = useState<{
    pdfs: Set<string>;
    pcs: Set<string>;
  } | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [d, ps, anns, pts, tls, inv, pcs, bms] = await Promise.all([
        api.get<Dossier>(`/dossiers/${id}`),
        api.get<PdfMetadata[]>(`/dossiers/${id}/pdfs`),
        api.get<Annotation[]>(`/dossiers/${id}/annotations`),
        api.get<Partie[]>(`/dossiers/${id}/parties`),
        api.get<TimelineEvent[]>(`/dossiers/${id}/timeline`),
        api.get<InventaireEntry[]>(`/dossiers/${id}/inventaire`),
        api.get<{ id: string; titre: string; date_piece: string | null }[]>(
          `/dossiers/${id}/pieces-client`
        ),
        api.get<Bookmark[]>(`/dossiers/${id}/bookmarks`).catch(() => [] as Bookmark[]),
      ]);
      setDossier(d);
      setPdfs(sortPieces(ps));
      setAnnotations(anns);
      setParties(pts);
      setTimelineEvents(tls);
      setInventaireEntries(inv);
      setPiecesClient(pcs);
      setBookmarks(bms);
      setError(null);
      // Refresh asynchrone du cumul IA (sans bloquer l'affichage du dossier).
      void loadAiCost();
      void loadIndexHealth();
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadIndexHealth = async () => {
    if (!id) return;
    try {
      const h = await api.get<{
        ratio: number;
        ocr_pending: number;
        fully_indexed: boolean;
        indexed_pages: number;
        text_pages: number;
      }>(`/dossiers/${id}/index-health`);
      setIndexHealth(h);
    } catch {
      setIndexHealth(null);
    }
  };

  const loadAiCost = async () => {
    if (!id) return;
    try {
      const c = await api.get<AiCostTotal>(`/dossiers/${id}/ai-cost-total`);
      setAiCost(c);
    } catch {
      // Pas critique : on cache simplement la chip si l'endpoint plante.
      setAiCost(null);
    }
  };

  useEffect(() => {
    void load();
    void loadAiCost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openMetaEdit = () => {
    if (!dossier) return;
    setMetaDraft({
      ref_cabinet: dossier.ref_cabinet,
      titre: dossier.titre ?? '',
      client_name: dossier.client_name ?? '',
      ref_parquet: dossier.ref_parquet ?? '',
      ref_forlex: dossier.ref_forlex ?? '',
      juridiction: dossier.juridiction ?? '',
      numero_role: dossier.numero_role ?? '',
      payment_mode: dossier.payment_mode ?? null,
    });
    setMetaError(null);
    setMetaOpen(true);
  };

  const saveMetaEdit = async () => {
    if (!id || !metaDraft) return;
    setMetaSaving(true);
    setMetaError(null);
    // PATCH n'envoie que les champs présents — on omet les vides plutôt
    // que de les forcer à null pour préserver les valeurs en base.
    const payload: Record<string, unknown> = {};
    const trim = (s: string) => s.trim();
    if (trim(metaDraft.ref_cabinet) && trim(metaDraft.ref_cabinet) !== dossier?.ref_cabinet)
      payload.ref_cabinet = trim(metaDraft.ref_cabinet);
    if (trim(metaDraft.titre) !== (dossier?.titre ?? ''))
      payload.titre = trim(metaDraft.titre) || null;
    if (trim(metaDraft.client_name) !== (dossier?.client_name ?? ''))
      payload.client_name = trim(metaDraft.client_name) || null;
    if (trim(metaDraft.ref_parquet) !== (dossier?.ref_parquet ?? ''))
      payload.ref_parquet = trim(metaDraft.ref_parquet) || null;
    if (trim(metaDraft.ref_forlex) !== (dossier?.ref_forlex ?? ''))
      payload.ref_forlex = trim(metaDraft.ref_forlex) || null;
    if (trim(metaDraft.juridiction) !== (dossier?.juridiction ?? ''))
      payload.juridiction = trim(metaDraft.juridiction) || null;
    if (trim(metaDraft.numero_role) !== (dossier?.numero_role ?? ''))
      payload.numero_role = trim(metaDraft.numero_role) || null;
    if (metaDraft.payment_mode !== (dossier?.payment_mode ?? null))
      payload.payment_mode = metaDraft.payment_mode;

    if (Object.keys(payload).length === 0) {
      setMetaOpen(false);
      setMetaSaving(false);
      return;
    }
    try {
      await api.patch(`/dossiers/${id}`, payload);
      setMetaOpen(false);
      toast.success('Métadonnées mises à jour');
      await load();
    } catch (err) {
      setMetaError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setMetaSaving(false);
    }
  };

  const onFilesSelected = async (files: File[]) => {
    if (!id) return;
    const initial: UploadStatus[] = files.map((f) => ({
      name: f.name,
      size: f.size,
      state: 'pending',
    }));
    setUploads(initial);
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i]!;
      // Pre-check: 0-byte typique d'un drag-drop depuis Outlook (pièce
      // jointe non matérialisée), un raccourci .lnk, OneDrive on-demand
      // non téléchargé, fichier locké. On rejette tôt avec un message
      // explicite plutôt qu'envoyer un multipart vide qui crashe gunicorn
      // en NoMoreData (cf. fix backend dans la même session).
      if (file.size === 0) {
        setUploads((rows) =>
          rows.map((r, idx) =>
            idx === i
              ? {
                  ...r,
                  state: 'failed',
                  detail:
                    'fichier vide ou non accessible — si tu l’as drag-droppé ' +
                    'depuis Outlook ou OneDrive, enregistre-le d’abord localement (Ctrl+S)',
                }
              : r
          )
        );
        continue;
      }
      setUploads((rows) => rows.map((r, idx) => (idx === i ? { ...r, state: 'uploading' } : r)));
      try {
        const fd = new FormData();
        fd.append('file', file);
        await api.postForm<PdfMetadata>(`/dossiers/${id}/pdfs`, fd);
        setUploads((rows) => rows.map((r, idx) => (idx === i ? { ...r, state: 'done' } : r)));
      } catch (err) {
        // Distinguer les vrais cas d'échec : erreur métier (ApiError → JSON
        // problem), erreur réseau (offline / TLS / connexion coupée — typique
        // sur très gros uploads), erreur générique.
        let detail: string;
        if (err instanceof ApiError) {
          detail = err.problem.detail ?? err.problem.title;
        } else if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          detail = `connexion réseau perdue pendant l'envoi (${(file.size / 1024 / 1024).toFixed(1)} Mo)`;
        } else {
          const msg = (err as Error)?.message ?? 'erreur inconnue';
          detail = `${msg} — fichier ${(file.size / 1024 / 1024).toFixed(1)} Mo`;
        }
        setUploads((rows) =>
          rows.map((r, idx) => (idx === i ? { ...r, state: 'failed', detail } : r))
        );
      }
    }
    await load();
  };

  const triggerExport = async () => {
    if (!id) return;
    setExporting(true);
    setExportStatus('Génération…');
    try {
      const body: Record<string, unknown> = { organization: exportOrg };
      if (exportSelector) {
        body.pdf_file_ids = Array.from(exportSelector.pdfs);
        body.piece_client_ids = Array.from(exportSelector.pcs);
      }
      const trigger = await api.post<ExportJobStatus>(`/dossiers/${id}/export`, body);
      const jobId = trigger.job_id;
      for (let i = 0; i < 60; i += 1) {
        const status = await api.get<ExportJobStatus>(`/exports/${jobId}`);
        if (status.status === 'done' && status.download_url) {
          const resp = await fetch(status.download_url, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `adr-${dossier?.ref_cabinet ?? 'dossier'}-notes.docx`;
          link.click();
          URL.revokeObjectURL(url);
          setExportStatus('Téléchargé');
          break;
        }
        if (status.status === 'failed') {
          setExportStatus(`Échec : ${status.error ?? 'inconnu'}`);
          break;
        }
        await new Promise((r) => setTimeout(r, 500));
      }
    } catch (err) {
      setExportStatus(err instanceof ApiError ? `Erreur : ${err.problem.title}` : 'Erreur réseau');
    } finally {
      setExporting(false);
    }
  };

  const progress = useMemo(() => {
    const totalPages = pdfs.reduce((sum, p) => sum + (p.page_count ?? 0), 0);
    if (totalPages === 0) return { treated: 0, total: 0, percent: 0 };
    const seen = new Set<string>();
    for (const a of annotations) {
      if (a.pdf_file_id && a.page_number) seen.add(`${a.pdf_file_id}:${a.page_number}`);
    }
    const treated = seen.size;
    return { treated, total: totalPages, percent: Math.round((treated / totalPages) * 100) };
  }, [pdfs, annotations]);

  const pagesTreatedByPdf = useMemo(() => {
    const m = new Map<string, Set<number>>();
    for (const a of annotations) {
      if (!a.pdf_file_id || !a.page_number) continue;
      const set = m.get(a.pdf_file_id) ?? new Set<number>();
      set.add(a.page_number);
      m.set(a.pdf_file_id, set);
    }
    return m;
  }, [annotations]);

  if (loading && !dossier) return <p className="text-sm text-text-secondary">Chargement…</p>;
  if (error && !dossier)
    return (
      <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
        {error}
      </p>
    );
  if (!dossier) return null;

  const stillUploading = uploads.some((u) => u.state === 'pending' || u.state === 'uploading');
  const annotationCount = annotations.length;
  const pieceCount = pdfs.length;

  /** Helper générique : fetch d'un blob authentifié → download forcé. */
  const downloadBlob = async (path: string, filename: string) => {
    const resp = await fetch(path, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || `HTTP ${resp.status}`);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /** Mini-dossier PDF avec inventaire en page 1 + pages réelles + fluo. */
  const exportBookmarks = async () => {
    if (!id) return;
    setExportingBookmarks(true);
    try {
      await downloadBlob(
        `/api/v1/dossiers/${id}/export-bookmarks`,
        `mini-dossier-${dossier?.ref_cabinet ?? 'adr'}.pdf`
      );
      toast.success('Mini-dossier PDF exporté');
    } catch (err) {
      toast.error((err as Error).message || 'Erreur réseau lors de l’export');
    } finally {
      setExportingBookmarks(false);
    }
  };

  /** POST avec body JSON puis téléchargement du blob retourné. */
  const downloadBlobPost = async (
    path: string,
    body: Record<string, unknown>,
    filename: string
  ) => {
    const resp = await fetch(path, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try {
        const j = (await resp.json()) as { detail?: string; title?: string };
        detail = j.detail ?? j.title ?? detail;
      } catch {
        // Pas du JSON — on garde HTTP <code>.
      }
      throw new Error(detail);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const generateMemoInterrogatoire = async () => {
    if (!id) return;
    setGeneratingMemo(true);
    try {
      const safeName = dossier?.client_name || dossier?.ref_cabinet || 'dossier';
      await downloadBlobPost(
        `/api/v1/dossiers/${id}/audience/memo-interrogatoire`,
        {},
        `memo-interrogatoire-${safeName.replace(/\W+/g, '_')}.docx`
      );
      toast.success('Mémo d’interrogatoire généré.');
    } catch (err) {
      toast.error((err as Error).message || 'Erreur génération mémo');
    } finally {
      setGeneratingMemo(false);
    }
  };

  const generateBriefingClient = async () => {
    if (!id) return;
    setGeneratingBriefing(true);
    try {
      const safeName = dossier?.client_name || dossier?.ref_cabinet || 'client';
      await downloadBlobPost(
        `/api/v1/dossiers/${id}/audience/briefing-client`,
        {
          juridiction: briefingOptions.juridiction || undefined,
          date_audience: briefingOptions.date_audience || undefined,
          posture: briefingOptions.posture || undefined,
          remarques_avocat: briefingOptions.remarques_avocat || undefined,
        },
        `briefing-client-${safeName.replace(/\W+/g, '_')}.docx`
      );
      toast.success('Briefing client généré.');
    } catch (err) {
      toast.error((err as Error).message || 'Erreur génération briefing');
    } finally {
      setGeneratingBriefing(false);
    }
  };

  /** Word de toutes les citations groupées par catégorie. */
  const exportCitations = async () => {
    if (!id) return;
    setExportingCitations(true);
    try {
      await downloadBlob(
        `/api/v1/dossiers/${id}/export-citations`,
        `citations-${dossier?.ref_cabinet ?? 'adr'}.docx`
      );
      toast.success('Citations exportées (Word)');
    } catch (err) {
      toast.error((err as Error).message || 'Erreur réseau lors de l’export');
    } finally {
      setExportingCitations(false);
    }
  };

  // TODO ADR-025 : ajouter un bouton « Reprendre la lecture » sur la
  // vue Lecture qui ouvre le dernier PDF consulté (mémorisé via
  // localStorage clé `adr.lastPdf.<dossierId>`).

  const linkPdfToEntry = async (pdfId: string, entryId: string | null) => {
    try {
      await api.patch<PdfMetadata>(`/pdfs/${pdfId}`, {
        inventaire_entry_id: entryId,
      });
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const renamePdf = async (pdfId: string, currentName: string) => {
    const next = window.prompt('Nouveau nom de la pièce', currentName);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === currentName) return;
    try {
      await api.patch<PdfMetadata>(`/pdfs/${pdfId}`, { filename_original: trimmed });
      toast.success('Pièce renommée');
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const removePdf = async (pdfId: string, currentName: string) => {
    if (await confirmAndDeletePdf(pdfId, currentName)) {
      await load();
    }
  };

  return (
    <div className="space-y-5">
      <Breadcrumb
        items={[
          { label: 'Dossiers', to: '/dossiers' },
          {
            label: `${dossier.client_name ?? dossier.titre ?? '—'} — ${formatDossierRef(dossier.ref_cabinet, dossier.payment_mode)}`,
          },
        ]}
      />

      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-[32px] font-medium leading-none text-text-primary">
              {dossier.client_name || dossier.titre || dossier.ref_cabinet}
            </h1>
            <div className="mt-2 text-[13px] text-text-secondary">
              <span className="font-mono">
                {formatDossierRef(dossier.ref_cabinet, dossier.payment_mode)}
              </span>
              {dossier.ref_parquet && (
                <>
                  {' · '}
                  <span className="font-mono">{dossier.ref_parquet}</span>
                </>
              )}
              {dossier.juridiction && <> · {dossier.juridiction}</>}
              {dossier.numero_role && <> · N° rôle {dossier.numero_role}</>}
            </div>
            {dossier.ai_extracted_metadata && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] italic text-text-tertiary">
                <span className="not-italic font-medium">Détecté par IA :</span>
                {dossier.ai_extracted_metadata.chambre && (
                  <span>{dossier.ai_extracted_metadata.chambre}</span>
                )}
                {dossier.ai_extracted_metadata.juge_instruction && (
                  <span>juge {dossier.ai_extracted_metadata.juge_instruction}</span>
                )}
                {dossier.ai_extracted_metadata.procureur && (
                  <span>parquet {dossier.ai_extracted_metadata.procureur}</span>
                )}
                {dossier.ai_extracted_metadata.greffier && (
                  <span>greffier {dossier.ai_extracted_metadata.greffier}</span>
                )}
                {dossier.ai_extracted_metadata.date_audience && (
                  <span>audience {dossier.ai_extracted_metadata.date_audience}</span>
                )}
                {typeof dossier.ai_extracted_metadata.confidence === 'number' && (
                  <span className="text-text-tertiary">
                    (confiance {Math.round((dossier.ai_extracted_metadata.confidence ?? 0) * 100)}
                    %)
                  </span>
                )}
              </div>
            )}
            <div className="mt-3 flex items-center gap-3.5 text-[13px] text-text-secondary">
              <span>
                <strong className="font-medium text-text-primary">{pieceCount}</strong> pièce
                {pieceCount > 1 ? 's' : ''}
              </span>
              <span className="h-[3px] w-[3px] rounded-full bg-rule-strong" aria-hidden />
              <span>
                <strong className="font-medium text-text-primary">{annotationCount}</strong>{' '}
                annotation
                {annotationCount > 1 ? 's' : ''}
              </span>
              {/* Compteur "parties" retiré du header (ADR-025). Le state
                  `parties` est conservé : il alimente encore Annotations
                  & citations / Contradictions / Recherche en attendant
                  la migration DB qui supprimera la table. */}
              {timelineCount > 0 && (
                <>
                  <span className="h-[3px] w-[3px] rounded-full bg-rule-strong" aria-hidden />
                  <span>
                    <strong className="font-medium text-text-primary">{timelineCount}</strong>{' '}
                    évènement{timelineCount > 1 ? 's' : ''}
                  </span>
                </>
              )}
              {progress.total > 0 && (
                <>
                  <span className="h-[3px] w-[3px] rounded-full bg-rule-strong" aria-hidden />
                  <span>
                    <strong className="font-medium text-text-primary">{progress.treated}</strong>/
                    {progress.total} page{progress.total > 1 ? 's' : ''} traitée
                    {progress.treated > 1 ? 's' : ''}
                  </span>
                </>
              )}
              {indexHealth && !indexHealth.fully_indexed && (
                <>
                  <span className="h-[3px] w-[3px] rounded-full bg-rule-strong" aria-hidden />
                  <span
                    className="inline-flex items-center gap-1 rounded-pill border border-cat-def/40 bg-cat-def-pastel px-2 py-0.5 text-[11px] font-medium text-cat-def"
                    title={`Indexation RAG incomplète : ${indexHealth.indexed_pages}/${indexHealth.text_pages} pages indexées${
                      indexHealth.ocr_pending > 0
                        ? `, ${indexHealth.ocr_pending} pièce(s) en attente d'OCR`
                        : ''
                    }. Relancer le digest pour une recherche exhaustive.`}
                  >
                    ⚠ Indexé {Math.round(indexHealth.ratio * 100)}%
                  </span>
                </>
              )}
              {aiCost && (
                <>
                  <span className="h-[3px] w-[3px] rounded-full bg-rule-strong" aria-hidden />
                  <button
                    type="button"
                    onClick={() => setShowAiCostDetails(!showAiCostDetails)}
                    className="inline-flex items-center gap-1 rounded-pill border border-rule bg-paper-elevated px-2 py-0.5 text-[11px] font-medium text-text-secondary transition hover:border-accent/40 hover:text-accent-text"
                    title="Coût cumulé IA pour ce dossier (chat, embeddings, import, analyse IA, générateurs, détecteurs, OCR Mistral)"
                  >
                    <span aria-hidden>✨</span>
                    <span>
                      IA :{' '}
                      <strong className="font-mono text-text-primary">
                        {aiCost.total_eur.toFixed(2)} €
                      </strong>
                    </span>
                    <span className="text-text-tertiary">{showAiCostDetails ? '▴' : '▾'}</span>
                  </button>
                </>
              )}
            </div>
            {showAiCostDetails && aiCost && (
              <div className="mt-2 max-w-md rounded-md border border-rule bg-paper-elevated p-3 text-[12px]">
                <p className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.5px] text-text-tertiary">
                  Cumul IA — détail
                </p>
                <ul className="space-y-1">
                  <li className="flex items-baseline justify-between gap-3">
                    <span className="text-text-secondary">
                      Chat (Sonnet 4.6) ·{' '}
                      <span className="text-text-tertiary">
                        {aiCost.breakdown.chat_messages.count} message
                        {aiCost.breakdown.chat_messages.count > 1 ? 's' : ''}
                      </span>
                    </span>
                    <span className="font-mono text-text-primary">
                      {aiCost.breakdown.chat_messages.eur.toFixed(3)} €
                    </span>
                  </li>
                  <li className="flex items-baseline justify-between gap-3">
                    <span className="text-text-secondary">
                      Auto-titres (Haiku 4.5) ·{' '}
                      <span className="text-text-tertiary">
                        {aiCost.breakdown.auto_title.count} session
                        {aiCost.breakdown.auto_title.count > 1 ? 's' : ''}
                      </span>
                    </span>
                    <span className="font-mono text-text-primary">
                      {aiCost.breakdown.auto_title.eur.toFixed(3)} €
                    </span>
                  </li>
                  <li className="flex items-baseline justify-between gap-3">
                    <span className="text-text-secondary">
                      Import inventaire (Haiku 4.5) ·{' '}
                      <span className="text-text-tertiary">
                        {aiCost.breakdown.inventaire_import.count} import
                        {aiCost.breakdown.inventaire_import.count > 1 ? 's' : ''}
                      </span>
                    </span>
                    <span className="font-mono text-text-primary">
                      {aiCost.breakdown.inventaire_import.eur.toFixed(3)} €
                    </span>
                  </li>
                  <li className="flex items-baseline justify-between gap-3">
                    <span className="text-text-secondary">
                      Indexation embeddings (Voyage Law-2) ·{' '}
                      <span className="text-text-tertiary">
                        {aiCost.breakdown.embeddings_indexation.count} page
                        {aiCost.breakdown.embeddings_indexation.count > 1 ? 's' : ''}
                      </span>
                    </span>
                    <span className="font-mono text-text-primary">
                      {aiCost.breakdown.embeddings_indexation.eur.toFixed(3)} €
                    </span>
                  </li>
                  {aiCost.breakdown.ai_research && aiCost.breakdown.ai_research.count > 0 && (
                    <li className="flex items-baseline justify-between gap-3">
                      <span className="text-text-secondary">
                        Analyse IA — 4 recherches (Sonnet 4.6) ·{' '}
                        <span className="text-text-tertiary">
                          {aiCost.breakdown.ai_research.count} recherche
                          {aiCost.breakdown.ai_research.count > 1 ? 's' : ''}
                        </span>
                      </span>
                      <span className="font-mono text-text-primary">
                        {aiCost.breakdown.ai_research.eur.toFixed(3)} €
                      </span>
                    </li>
                  )}
                  {aiCost.breakdown.conclusions_gen &&
                    aiCost.breakdown.conclusions_gen.count > 0 && (
                      <li className="flex items-baseline justify-between gap-3">
                        <span className="text-text-secondary">
                          Conclusions générées (Sonnet 4.6) ·{' '}
                          <span className="text-text-tertiary">
                            {aiCost.breakdown.conclusions_gen.count} conclusion
                            {aiCost.breakdown.conclusions_gen.count > 1 ? 's' : ''}
                          </span>
                        </span>
                        <span className="font-mono text-text-primary">
                          {aiCost.breakdown.conclusions_gen.eur.toFixed(3)} €
                        </span>
                      </li>
                    )}
                  {aiCost.breakdown.actes_gen && aiCost.breakdown.actes_gen.count > 0 && (
                    <li className="flex items-baseline justify-between gap-3">
                      <span className="text-text-secondary">
                        Actes générés (Sonnet 4.6) ·{' '}
                        <span className="text-text-tertiary">
                          {aiCost.breakdown.actes_gen.count} acte
                          {aiCost.breakdown.actes_gen.count > 1 ? 's' : ''}
                        </span>
                      </span>
                      <span className="font-mono text-text-primary">
                        {aiCost.breakdown.actes_gen.eur.toFixed(3)} €
                      </span>
                    </li>
                  )}
                  {aiCost.breakdown.briefings_gen && aiCost.breakdown.briefings_gen.count > 0 && (
                    <li className="flex items-baseline justify-between gap-3">
                      <span className="text-text-secondary">
                        Briefings clients (Sonnet 4.6) ·{' '}
                        <span className="text-text-tertiary">
                          {aiCost.breakdown.briefings_gen.count} briefing
                          {aiCost.breakdown.briefings_gen.count > 1 ? 's' : ''}
                        </span>
                      </span>
                      <span className="font-mono text-text-primary">
                        {aiCost.breakdown.briefings_gen.eur.toFixed(3)} €
                      </span>
                    </li>
                  )}
                  {aiCost.breakdown.memos_gen && aiCost.breakdown.memos_gen.count > 0 && (
                    <li className="flex items-baseline justify-between gap-3">
                      <span className="text-text-secondary">
                        Mémos interrogatoire (Sonnet 4.6) ·{' '}
                        <span className="text-text-tertiary">
                          {aiCost.breakdown.memos_gen.count} mémo
                          {aiCost.breakdown.memos_gen.count > 1 ? 's' : ''}
                        </span>
                      </span>
                      <span className="font-mono text-text-primary">
                        {aiCost.breakdown.memos_gen.eur.toFixed(3)} €
                      </span>
                    </li>
                  )}
                  {aiCost.breakdown.detections && aiCost.breakdown.detections.count > 0 && (
                    <li className="flex items-baseline justify-between gap-3">
                      <span className="text-text-secondary">
                        Détecteurs (contradictions/procédural) ·{' '}
                        <span className="text-text-tertiary">
                          {aiCost.breakdown.detections.count} run
                          {aiCost.breakdown.detections.count > 1 ? 's' : ''}
                        </span>
                      </span>
                      <span className="font-mono text-text-primary">
                        {aiCost.breakdown.detections.eur.toFixed(3)} €
                      </span>
                    </li>
                  )}
                  {aiCost.breakdown.mistral_ocr && aiCost.breakdown.mistral_ocr.count > 0 && (
                    <li className="flex items-baseline justify-between gap-3">
                      <span className="text-text-secondary">
                        OCR Mistral (gros PDFs scannés) ·{' '}
                        <span className="text-text-tertiary">
                          {aiCost.breakdown.mistral_ocr.count} call
                          {aiCost.breakdown.mistral_ocr.count > 1 ? 's' : ''}
                        </span>
                      </span>
                      <span className="font-mono text-text-primary">
                        {aiCost.breakdown.mistral_ocr.eur.toFixed(3)} €
                      </span>
                    </li>
                  )}
                  <li className="mt-1 flex items-baseline justify-between gap-3 border-t border-rule pt-1.5 font-medium">
                    <span className="text-text-primary">Total</span>
                    <span className="font-mono text-text-primary">
                      {aiCost.total_eur.toFixed(3)} € ·{' '}
                      <span className="text-text-tertiary">${aiCost.total_usd.toFixed(3)}</span>
                    </span>
                  </li>
                </ul>
                <p className="mt-2 text-[10px] text-text-tertiary">
                  Chat utilise les vrais tokens Anthropic depuis le Sprint 3 ; les usages antérieurs
                  sont estimés à partir des chars saisis.
                </p>
              </div>
            )}
            {progress.total > 0 && (
              <div className="mt-2.5 max-w-md">
                <div
                  className="relative h-2 overflow-hidden rounded-full bg-paper-subtle"
                  role="progressbar"
                  aria-valuenow={progress.percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Progression analyse du dossier"
                >
                  <div
                    className="h-full rounded-full bg-accent transition-[width] duration-500 ease-out"
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.3px] text-text-tertiary">
                  Analyse · {progress.percent}%
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <StatusBadge variant={dossier.status === 'active' ? 'active' : 'archived'}>
              {dossier.status === 'active' ? 'Actif' : dossier.status}
            </StatusBadge>
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => openMetaEdit()}
              title="Modifier les métadonnées du dossier (nom, références…)"
            >
              ✏ Modifier
            </Btn>
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => setAccessOpen((v) => !v)}
              title="Modifier qui a accès à ce dossier"
            >
              👥 Accès ({dossier.access_mode === 'all' ? 'cabinet' : 'restreint'})
            </Btn>
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => setAudienceOpen((v) => !v)}
              title="Générer mémo d'interrogatoire et briefing client"
            >
              ⚖️ Préparation audience
            </Btn>
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => window.open(`/dossiers/${id}/audience`, '_blank')}
              title="Ouvrir le viewer plein écran optimisé tablette / S Pen (à scanner ou copier sur la tablette)"
            >
              📱 Mode audience (tablette)
            </Btn>
            {/* Import / sync depuis Google Drive (ForlexDocuments) — scanne tout
                le dossier Drive et importe les pièces pas encore dans ADR.
                Sens unique Drive → ADR. */}
            <GDriveSyncButton dossierId={id!} onImported={() => void load()} />
            {/* Range automatiquement les pièces du vrac (dossier répressif)
                vers Pièces client / Pièces adverses via classification IA. */}
            <AutoSortButton dossierId={id!} onSorted={() => void load()} />
            {canDeleteDossier && (
              <Btn
                variant="ghost"
                size="sm"
                onClick={onDeleteDossier}
                title="Mettre le dossier à la corbeille (restaurable 30 jours, puis purge définitive). Admin uniquement."
                className="text-cat-def hover:bg-cat-def-pastel"
              >
                🗑 Supprimer le dossier
              </Btn>
            )}
          </div>
        </div>
      </div>

      {/* Bouton Analyse IA — hub central, mis en évidence sous le header dossier */}
      <div className="rounded-lg border-2 border-accent/40 bg-gradient-to-r from-accent-lighter via-paper-elevated to-paper-elevated p-1 shadow-md">
        <AnalyseIaButton dossierId={id!} />
      </div>

      {audienceOpen && (
        <div className="rounded-lg border border-rule bg-paper-elevated p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[14px] font-medium text-text-primary">
              ⚖️ Préparation audience — IA
            </h2>
            <button
              type="button"
              onClick={() => setAudienceOpen(false)}
              className="rounded p-0.5 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
              aria-label="Fermer"
              title="Fermer"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-md border border-rule bg-paper-card p-4">
              <h3 className="font-serif text-[14px] font-medium text-text-primary">
                📓 Mémo d&apos;interrogatoire
              </h3>
              <p className="mt-1 text-[12px] text-text-tertiary">
                Document interne pour vous : questions clés par partie, moyens de procédure à
                plaider, chronologie critique, contradictions à exploiter. Basé sur les parties, la
                timeline, les alertes et contradictions déjà détectées.
              </p>
              <div className="mt-3">
                <Btn
                  variant="primary"
                  size="sm"
                  onClick={() => void generateMemoInterrogatoire()}
                  disabled={generatingMemo || generatingBriefing}
                >
                  {generatingMemo ? 'Génération…' : 'Générer le mémo (.docx)'}
                </Btn>
              </div>
            </div>

            <div className="rounded-md border border-rule bg-paper-card p-4">
              <h3 className="font-serif text-[14px] font-medium text-text-primary">
                🤝 Briefing client
              </h3>
              <p className="mt-1 text-[12px] text-text-tertiary">
                Document à remettre au client avant l&apos;audience : déroulé concret, attitude, ce
                qu&apos;il faut dire ou éviter. Ton vulgarisé, pas de jargon.
              </p>
              <div className="mt-3 space-y-2">
                <input
                  type="text"
                  value={briefingOptions.juridiction}
                  onChange={(e) =>
                    setBriefingOptions((s) => ({ ...s, juridiction: e.target.value }))
                  }
                  placeholder={`Juridiction (défaut: ${dossier.juridiction ?? 'non renseignée'})`}
                  className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12px]"
                />
                <input
                  type="date"
                  value={briefingOptions.date_audience}
                  onChange={(e) =>
                    setBriefingOptions((s) => ({ ...s, date_audience: e.target.value }))
                  }
                  className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12px]"
                  title="Date d'audience (optionnel)"
                />
                <select
                  value={briefingOptions.posture}
                  onChange={(e) => setBriefingOptions((s) => ({ ...s, posture: e.target.value }))}
                  className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12px]"
                >
                  <option value="">— posture (optionnel) —</option>
                  <option value="contestation">Contestation totale</option>
                  <option value="contestation_partielle">Contestation partielle</option>
                  <option value="aveu_partiel">Aveu partiel</option>
                  <option value="negociation">Négociation</option>
                  <option value="non_lieu">Plaidoyer non-lieu</option>
                </select>
                <textarea
                  value={briefingOptions.remarques_avocat}
                  onChange={(e) =>
                    setBriefingOptions((s) => ({ ...s, remarques_avocat: e.target.value }))
                  }
                  placeholder="Remarques pour adapter le ton (optionnel)"
                  rows={2}
                  className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[12px]"
                />
                <Btn
                  variant="primary"
                  size="sm"
                  onClick={() => void generateBriefingClient()}
                  disabled={generatingMemo || generatingBriefing}
                >
                  {generatingBriefing ? 'Génération…' : 'Générer le briefing (.docx)'}
                </Btn>
              </div>
            </div>
          </div>
          <p className="mt-3 text-[10px] text-text-tertiary">
            Les deux documents sont générés par Claude Sonnet 4.6 à partir du contexte du dossier. À{' '}
            <strong>relire et adapter</strong> avant usage : l&apos;IA peut se tromper.
          </p>
        </div>
      )}

      {metaOpen && metaDraft && (
        <div className="rounded-lg border border-rule bg-paper-elevated p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[14px] font-medium text-text-primary">
              Modifier les métadonnées du dossier
            </h2>
            <button
              type="button"
              onClick={() => {
                setMetaOpen(false);
                setMetaError(null);
              }}
              className="rounded p-0.5 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
              aria-label="Fermer"
              title="Annuler"
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
                Référence cabinet
              </span>
              <input
                value={metaDraft.ref_cabinet}
                onChange={(e) =>
                  setMetaDraft((d) => (d ? { ...d, ref_cabinet: e.target.value } : d))
                }
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-1.5 font-mono text-sm focus:border-accent focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
                Nom du dossier
              </span>
              <input
                value={metaDraft.titre}
                onChange={(e) => setMetaDraft((d) => (d ? { ...d, titre: e.target.value } : d))}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-1.5 text-sm focus:border-accent focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
                Nom de la partie / client
              </span>
              <input
                value={metaDraft.client_name}
                onChange={(e) =>
                  setMetaDraft((d) => (d ? { ...d, client_name: e.target.value } : d))
                }
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-1.5 text-sm focus:border-accent focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
                Référence parquet
              </span>
              <input
                value={metaDraft.ref_parquet}
                onChange={(e) =>
                  setMetaDraft((d) => (d ? { ...d, ref_parquet: e.target.value } : d))
                }
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-1.5 font-mono text-sm focus:border-accent focus:outline-none"
              />
            </label>
            {/* Ref Forlex : masquée si identique au ref cabinet (cas courant
                 — le cabinet utilise la même référence dans les deux outils).
                 Bouton « Définir une ref Forlex distincte » pour rouvrir. */}
            {metaDraft.ref_forlex && metaDraft.ref_forlex !== metaDraft.ref_cabinet ? (
              <label className="block">
                <span className="block text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
                  Référence Forlex (distincte du ref cabinet)
                </span>
                <input
                  value={metaDraft.ref_forlex}
                  onChange={(e) =>
                    setMetaDraft((d) => (d ? { ...d, ref_forlex: e.target.value } : d))
                  }
                  className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-1.5 font-mono text-sm focus:border-accent focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setMetaDraft((d) => (d ? { ...d, ref_forlex: d.ref_cabinet } : d))}
                  className="mt-1 text-[11px] text-accent-text hover:underline"
                >
                  ↺ Réutiliser la référence cabinet
                </button>
              </label>
            ) : (
              <div className="text-[11px] text-text-tertiary">
                <span>Référence Forlex = </span>
                <span className="font-mono">{metaDraft.ref_cabinet || '(ref cabinet)'}</span>
                <button
                  type="button"
                  onClick={() =>
                    setMetaDraft((d) =>
                      d ? { ...d, ref_forlex: d.ref_forlex || d.ref_cabinet + '-fx' } : d
                    )
                  }
                  className="ml-2 text-accent-text hover:underline"
                >
                  Définir une ref Forlex distincte
                </button>
              </div>
            )}
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
                Juridiction
              </span>
              <input
                list="juridictions-be"
                value={metaDraft.juridiction}
                onChange={(e) =>
                  setMetaDraft((d) => (d ? { ...d, juridiction: e.target.value } : d))
                }
                placeholder="Commencer à taper : Cass, Cour d'appel Liège, Tribunal correctionnel…"
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-1.5 text-sm focus:border-accent focus:outline-none"
              />
              <datalist id="juridictions-be">
                {JURIDICTIONS_BE.map((j) => (
                  <option key={j} value={j} />
                ))}
              </datalist>
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
                N° de rôle
              </span>
              <input
                value={metaDraft.numero_role}
                onChange={(e) =>
                  setMetaDraft((d) => (d ? { ...d, numero_role: e.target.value } : d))
                }
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-1.5 font-mono text-sm focus:border-accent focus:outline-none"
              />
            </label>
            <fieldset className="block">
              <legend className="text-[10px] font-medium uppercase tracking-[1px] text-text-secondary">
                Mode de paiement
              </legend>
              <div className="mt-1 flex flex-wrap gap-3 text-[13px]">
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="meta_payment"
                    checked={metaDraft.payment_mode === null}
                    onChange={() => setMetaDraft((d) => (d ? { ...d, payment_mode: null } : d))}
                  />
                  Non précisé
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="meta_payment"
                    checked={metaDraft.payment_mode === 'paying'}
                    onChange={() => setMetaDraft((d) => (d ? { ...d, payment_mode: 'paying' } : d))}
                  />
                  Payant
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="meta_payment"
                    checked={metaDraft.payment_mode === 'baj'}
                    onChange={() => setMetaDraft((d) => (d ? { ...d, payment_mode: 'baj' } : d))}
                  />
                  BAJ
                </label>
              </div>
            </fieldset>
          </div>
          {metaError && <p className="mt-3 text-xs text-cat-def">{metaError}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <Btn variant="ghost" size="sm" onClick={() => setMetaOpen(false)}>
              Annuler
            </Btn>
            <Btn
              variant="primary"
              size="sm"
              disabled={metaSaving}
              onClick={() => void saveMetaEdit()}
            >
              {metaSaving ? 'Enregistrement…' : 'Enregistrer'}
            </Btn>
          </div>
        </div>
      )}

      {accessOpen && (
        <DossierAccessPanel
          dossierId={id!}
          accessMode={dossier.access_mode ?? 'all'}
          onClose={() => setAccessOpen(false)}
          onChange={() => void load()}
        />
      )}

      {bulkExportOpen && (
        <BulkExportModal
          dossierId={id!}
          dossierRef={dossier.ref_cabinet}
          onClose={() => setBulkExportOpen(false)}
        />
      )}

      {migratePdf && (
        <MigratePieceDialog
          pdfId={migratePdf.id}
          pdfTitle={migratePdf.title}
          onClose={() => setMigratePdf(null)}
          onMigrated={() => void load()}
        />
      )}

      {/* Nav top-level — 5 onglets (ADR-025). */}
      <nav
        className="flex gap-1 border-b border-rule overflow-x-auto"
        aria-label="Sections du dossier"
      >
        {TOP_TABS.map((t) => {
          const isActive = t.id === activeTop;
          const badge = topTabBadge(t.id, {
            pdfs: pieceCount,
            annotations: annotationCount,
            timeline: timelineCount,
            bookmarks: bookmarks.length,
          });
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => navigateToTop(t.id)}
              className={`-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-[14px] transition ${
                isActive
                  ? 'border-text-primary font-medium text-text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              <t.Icon size={16} stroke={1.6} />
              {t.label}
              {badge !== null && (
                <span className="inline-flex items-center justify-center rounded-pill bg-accent-light px-1.5 text-[10px] font-medium text-accent-text">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Sous-nav (Dossier / Analyse / Outils IA). */}
      {SUB_TABS[activeTop].length > 0 && (
        <nav
          className="flex gap-1 border-b border-rule bg-paper-subtle px-2 overflow-x-auto"
          aria-label={`Sous-sections de ${TOP_TABS.find((x) => x.id === activeTop)?.label}`}
        >
          {SUB_TABS[activeTop].map((s) => {
            const isActive = s.id === activeSub;
            const badge = subTabBadge(s.id, {
              pdfs: pieceCount,
              annotations: annotationCount,
              timeline: timelineCount,
              bookmarks: bookmarks.length,
            });
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => navigateToSub(activeTop, s.id)}
                className={`-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-[1.5px] px-3 py-2 text-[12.5px] transition ${
                  isActive
                    ? 'border-text-primary font-medium text-text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
                title={s.label}
              >
                <s.Icon size={14} stroke={1.6} />
                {/* Responsive iPad : version courte si la fenêtre est étroite. */}
                {s.labelShort ? (
                  <>
                    <span className="hidden lg:inline">{s.label}</span>
                    <span className="inline lg:hidden">{s.labelShort}</span>
                  </>
                ) : (
                  s.label
                )}
                {badge !== null && (
                  <span className="inline-flex items-center justify-center rounded-pill bg-accent-light px-1.5 text-[10px] font-medium text-accent-text">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      )}

      {activeTop === 'lecture' ? (
        pdfs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-rule bg-paper-subtle p-10 text-center">
            <p className="font-serif text-[18px] text-text-primary">Aucune pièce dans le dossier</p>
            <p className="mt-2 text-[13px] text-text-secondary">
              La lecture démarre quand tu importes un premier PDF.
            </p>
            <Btn
              variant="primary"
              onClick={() => navigateToSub('dossier', 'pieces')}
              className="mt-4"
            >
              Aller dans Dossier · Pièces
            </Btn>
          </div>
        ) : (
          (() => {
            // Tri inventaire (ADR-039) : utilise la préférence persistée
            // dans localStorage `adr_inv_pref_<dossierId>` (auto / manual /
            // ai_parsed). Fallback sortPieces si pas d'inventaire.
            const pref: InventorySourcePreference = (() => {
              const s = id ? localStorage.getItem(`adr_inv_pref_${id}`) : null;
              return s === 'manual' || s === 'ai_parsed' || s === 'auto' ? s : 'auto';
            })();
            const entryById = new Map(inventaireEntries.map((e) => [e.id, e]));
            const sorted =
              inventaireEntries.length > 0
                ? sortPiecesByInventory(pdfs, inventaireEntries, pref)
                : sortPieces(pdfs);

            // Statut de chaque pièce dérivé des annotations existantes :
            //   blanc  = aucune page annotée
            //   orange = au moins 1 page annotée mais pas toutes
            //   vert   = toutes les pages ont au moins 1 annotation
            const pagesByPdf = new Map<string, Set<number>>();
            for (const a of annotations) {
              if (!a.pdf_file_id || !a.page_number) continue;
              const set = pagesByPdf.get(a.pdf_file_id) ?? new Set<number>();
              set.add(a.page_number);
              pagesByPdf.set(a.pdf_file_id, set);
            }
            const statusOf = (p: PdfMetadata): 'untouched' | 'in_progress' | 'done' => {
              // J1 — le statut explicite override la dérivation automatique.
              if (p.review_status === 'analysee') return 'done';
              if (p.review_status === 'a_relire') return 'in_progress';
              const annPages = pagesByPdf.get(p.id);
              if (!annPages || annPages.size === 0) return 'untouched';
              if (!p.page_count || p.page_count === 0) return 'in_progress';
              return annPages.size >= p.page_count ? 'done' : 'in_progress';
            };
            const dotClass = {
              untouched: 'bg-paper-elevated border border-rule-strong',
              in_progress: 'bg-cat-imp/40 border border-cat-imp/60',
              done: 'bg-cat-fav/40 border border-cat-fav/60',
            } as const;
            const rowClass = {
              untouched: 'border-rule hover:bg-paper-subtle',
              in_progress: 'border-cat-imp/30 bg-cat-imp/5 hover:bg-cat-imp/10',
              done: 'border-cat-fav/30 bg-cat-fav/5 hover:bg-cat-fav/10',
            } as const;

            const counts = sorted.reduce(
              (acc, p) => {
                acc[statusOf(p)] += 1;
                return acc;
              },
              { untouched: 0, in_progress: 0, done: 0 }
            );

            // Marqueurs par pièce : on compte page-marks + un flag pour
            // pièce-entière. Affichés sous forme de badge à droite du titre.
            const bmByPdf = new Map<string, { pages: number; whole: boolean }>();
            for (const b of bookmarks) {
              if (!b.pdf_file_id) continue;
              const cur = bmByPdf.get(b.pdf_file_id) ?? { pages: 0, whole: false };
              if (b.page_number === null) cur.whole = true;
              else cur.pages += 1;
              bmByPdf.set(b.pdf_file_id, cur);
            }
            const totalBm = bookmarks.length;

            return (
              <div className="space-y-4">
                <div className="rounded-lg border border-rule bg-paper-card p-5">
                  <div className="flex items-center justify-between">
                    <h2 className="font-serif text-[20px] text-text-primary">🔎 Analyse</h2>
                    <div className="flex items-center gap-3 text-[12px] text-text-secondary">
                      <span className="flex items-center gap-1.5">
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-full ${dotClass.done}`}
                        />
                        {counts.done} terminée{counts.done > 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-full ${dotClass.in_progress}`}
                        />
                        {counts.in_progress} en cours
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-full ${dotClass.untouched}`}
                        />
                        {counts.untouched} à faire
                      </span>
                    </div>
                  </div>
                  <p className="mt-1 text-[13px] text-text-secondary">
                    {pdfs.length} pièce{pdfs.length > 1 ? 's' : ''} — clique pour ouvrir.
                    {inventaireEntries.length > 0
                      ? ' Tri par inventaire greffe.'
                      : ' Tri alphanumérique (pas d’inventaire).'}
                    {totalBm > 0 && (
                      <span className="ml-1 text-text-primary">
                        · {totalBm} marqueur{totalBm > 1 ? 's' : ''} 🔖
                      </span>
                    )}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Btn
                      variant="primary"
                      size="sm"
                      onClick={() => setBulkExportOpen(true)}
                      title="Télécharger toutes les pièces du dossier dans un ZIP organisé par catégorie"
                    >
                      📥 Télécharger tout (ZIP)
                    </Btn>
                    {totalBm > 0 && (
                      <Btn
                        variant="secondary"
                        size="sm"
                        onClick={() => void exportBookmarks()}
                        disabled={exportingBookmarks}
                        title="Générer un PDF avec inventaire en page 1 + pages réelles des marqueurs (avec fluo)"
                      >
                        {exportingBookmarks ? 'Export…' : '📦 Mini-dossier PDF'}
                      </Btn>
                    )}
                    {annotationCount > 0 && (
                      <Btn
                        variant="secondary"
                        size="sm"
                        onClick={() => void exportCitations()}
                        disabled={exportingCitations}
                        title="Exporter toutes les annotations groupées par catégorie (Word)"
                      >
                        {exportingCitations ? 'Export…' : '📄 Citations (Word)'}
                      </Btn>
                    )}
                  </div>
                </div>

                <ul className="grid grid-cols-1 gap-2 lg:grid-cols-2 xl:grid-cols-3">
                  {sorted.map((p) => {
                    const st = statusOf(p);
                    const e = p.inventaire_entry_id
                      ? entryById.get(p.inventaire_entry_id)
                      : undefined;
                    const annCount = pagesByPdf.get(p.id)?.size ?? 0;
                    const bm = bmByPdf.get(p.id);
                    return (
                      <li key={p.id}>
                        <Link
                          to={`/dossiers/${id}/pieces/${p.id}`}
                          onClick={() => {
                            if (typeof window !== 'undefined')
                              window.localStorage.setItem(LAST_PDF_KEY(id!), p.id);
                          }}
                          className={`group flex items-start gap-3 rounded-md border p-3 text-[13px] no-underline transition ${rowClass[st]}`}
                          title={p.filename_original}
                        >
                          <span
                            className={`mt-0.5 h-3 w-3 shrink-0 rounded-full ${dotClass[st]}`}
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <span className="block truncate font-medium text-text-primary">
                                {p.filename_original}
                              </span>
                              {bm && (bm.whole || bm.pages > 0) && (
                                <span
                                  className="shrink-0 rounded-pill bg-paper-subtle px-1.5 text-[10px] font-medium text-text-primary"
                                  title={`${bm.whole ? 'Pièce marquée. ' : ''}${bm.pages > 0 ? `${bm.pages} page${bm.pages > 1 ? 's' : ''} marquée${bm.pages > 1 ? 's' : ''}.` : ''}`}
                                >
                                  🔖{bm.whole ? '★' : bm.pages}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  void renamePdf(p.id, p.filename_original);
                                }}
                                className="ml-auto shrink-0 rounded p-0.5 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-paper-subtle hover:text-text-primary"
                                title="Renommer la pièce"
                              >
                                ✏
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setMigratePdf({ id: p.id, title: p.filename_original });
                                }}
                                className="shrink-0 rounded border border-accent/30 bg-accent-light px-1.5 py-0.5 text-[11px] text-accent-text transition hover:bg-accent hover:text-paper-elevated"
                                title="Migrer vers un autre bucket (Pièces client / Adverses / Conclusions / Autres)"
                              >
                                ↗ Ranger
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  void removePdf(p.id, p.filename_original);
                                }}
                                className="shrink-0 rounded p-0.5 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-cat-def-pastel hover:text-cat-def"
                                title="Supprimer la pièce"
                              >
                                🗑
                              </button>
                            </span>
                            <span className="mt-0.5 block text-[11px] text-text-tertiary">
                              {e
                                ? `${e.carton ? `Carton ${e.carton}` : ''}${e.sous_farde ? ` · farde ${e.sous_farde}` : ''}${e.piece ? ` · pièce ${e.piece}` : ''}`
                                : '— non coté'}
                              {p.page_count ? ` · ${p.page_count} p.` : ''}
                              {annCount > 0
                                ? ` · ${annCount} page${annCount > 1 ? 's' : ''} annotée${annCount > 1 ? 's' : ''}`
                                : ''}
                            </span>
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })()
        )
      ) : activeSub === 'annotations-citations' ? (
        <AnnotationsCitationsTab
          dossierId={id!}
          pdfs={pdfs}
          annotations={annotations}
          onAnnotationsChange={load}
        />
      ) : activeSub === 'inventaire' ? (
        <InventaireTab dossierId={id!} pdfs={pdfs} onChange={() => void load()} />
      ) : activeSub === 'parties' ? (
        <PartiesTab dossierId={id!} pdfs={pdfs} parties={parties} onChange={() => void load()} />
      ) : activeSub === 'pieces-client' ? (
        <PiecesClientTab dossierId={id!} />
      ) : activeSub === 'pieces-adverses' ? (
        <PiecesAdversesTab dossierId={id!} />
      ) : activeSub === 'conclusions' ? (
        // Sous Dossier : storage des conclusions reçues/envoyées (≠ générateur IA).
        <ConclusionsStorageTab dossierId={id!} />
      ) : activeSub === 'autres' ? (
        <OtherDocumentsTab dossierId={id!} />
      ) : activeSub === 'timeline' ? (
        <TimelineTab dossierId={id!} pdfs={pdfs} onEventsChange={setTimelineEvents} />
      ) : activeSub === 'synthese' ? (
        <AiResearchTab dossierId={id!} filterType="synthese" />
      ) : activeSub === 'charges' ? (
        <AiResearchTab dossierId={id!} filterType="charges" />
      ) : activeSub === 'decharges' ? (
        <AiResearchTab dossierId={id!} filterType="decharges" />
      ) : activeSub === 'droit' ? (
        <AiResearchTab dossierId={id!} filterType="droit" />
      ) : activeSub === 'briefing-client' ? (
        <BriefingsTab dossierId={id!} />
      ) : activeSub === 'memo-interrogatoire' ? (
        <MemoInterrogatoireTab dossierId={id!} />
      ) : activeSub === 'note-generale' ? (
        <NoteGeneraleTab
          dossierId={id!}
          initial={dossier?.general_note ?? null}
          onSaved={() => void load()}
        />
      ) : activeSub === 'mini-dossier' ? (
        <MiniDossierTab
          dossierId={id!}
          ref_cabinet={dossier?.ref_cabinet ?? null}
          bookmarks={bookmarks}
          pdfs={pdfs}
          annotations={annotations}
          onChange={() => void load()}
        />
      ) : activeSub === 'coherence' ? (
        <CoherenceTab dossierId={id!} />
      ) : activeSub === 'contradictions' ? (
        <ContradictionsTab
          dossierId={id!}
          parties={parties}
          onOpenPdf={(pdfId, page) => navigate(`/dossiers/${id}/pieces/${pdfId}?page=${page}`)}
        />
      ) : activeTop === 'etat' ? (
        <EtatProcedureTab dossierId={id!} />
      ) : activeSub === 'rediger-conclusions' ? (
        <ConclusionsTab dossierId={id!} />
      ) : activeSub === 'actes-procedure' ? (
        <ActesGeneratorTab dossierId={id!} />
      ) : activeTop === 'journal' ? (
        <JournalTab dossierId={id!} />
      ) : activeTop === 'outils-ia' ? (
        <div className="space-y-3">
          <header>
            <h2 className="text-[16px] font-medium text-text-primary">Outils IA</h2>
            <p className="mt-1 text-[12.5px] text-text-secondary">
              Scans automatiques propulsés par Claude Sonnet. Chaque outil produit des résultats à
              vérifier manuellement avant utilisation.
            </p>
          </header>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <AiToolCard
              icon={<IconSparkles size={20} stroke={1.6} />}
              title="Synthèse IA"
              description="Synthèse exhaustive du dossier (faits, procédure, pièces, points forts/faibles)."
              onOpen={() => navigateToSub('outils-ia', 'synthese')}
            />
            <AiToolCard
              icon={<IconChecks size={20} stroke={1.6} />}
              title="Cohérence du corpus"
              description="Liste les PV, annexes et cotes cités dans le texte mais absents du dossier (à demander au parquet)."
              onScan={() => navigateToSub('outils-ia', 'coherence')}
            />
            <AiToolCard
              icon={<IconGavel size={20} stroke={1.6} />}
              title="Droit (questions juridiques)"
              description="Liste les questions de droit pertinentes (nullités, éléments constitutifs, sanction…) — niveau avocat, cochables pour réutilisation."
              onScan={() => navigateToSub('outils-ia', 'droit')}
            />
            <AiToolCard
              icon={<IconArrowsCross size={20} stroke={1.6} />}
              title="Contradictions"
              description="Cherche les contradictions factuelles entre auditions d'une même personne ET entre personnes différentes."
              onScan={() => navigateToSub('outils-ia', 'contradictions')}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto_auto] md:items-center">
            <select className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm">
              <option>Tous versements</option>
            </select>
            <select className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm">
              <option>OCR : tous</option>
            </select>
            <select
              value={exportOrg}
              onChange={(e) =>
                setExportOrg(e.target.value as 'by_category' | 'by_document' | 'by_party')
              }
              className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm"
              title="Organisation du document Word généré"
            >
              <option value="by_category">Par catégorie</option>
              <option value="by_document">Par pièce (audience)</option>
              <option value="by_party">Par partie (plaidoirie)</option>
            </select>
            <div className="flex items-center gap-1.5">
              <Btn
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (exportSelector) {
                    setExportSelector(null);
                  } else {
                    setExportSelector({
                      pdfs: new Set(pdfs.map((p) => p.id)),
                      pcs: new Set(piecesClient.map((p) => p.id)),
                    });
                  }
                }}
                title="Choisir les pièces incluses dans l'export"
              >
                {exportSelector
                  ? `Sélection : ${exportSelector.pdfs.size}+${exportSelector.pcs.size}`
                  : 'Choisir les pièces…'}
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                onClick={triggerExport}
                disabled={exporting || annotationCount === 0}
              >
                {exporting ? 'Export…' : 'Exporter Word — dossier'}
              </Btn>
            </div>
          </div>
          {exportSelector && (
            <div className="rounded-md border border-rule bg-paper-elevated p-3 text-sm">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-medium text-text-primary">Choisir les pièces à exporter</span>
                <div className="flex gap-1.5">
                  <button
                    onClick={() =>
                      setExportSelector({
                        pdfs: new Set(pdfs.map((p) => p.id)),
                        pcs: new Set(piecesClient.map((p) => p.id)),
                      })
                    }
                    className="rounded px-2 py-1 text-[11px] text-text-secondary hover:bg-paper-subtle"
                  >
                    Tout cocher
                  </button>
                  <button
                    onClick={() => setExportSelector({ pdfs: new Set(), pcs: new Set() })}
                    className="rounded px-2 py-1 text-[11px] text-text-secondary hover:bg-paper-subtle"
                  >
                    Tout décocher
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-[0.3px] text-text-tertiary">
                    PDFs greffe ({pdfs.length})
                  </p>
                  <ul className="max-h-40 overflow-y-auto space-y-0.5">
                    {pdfs.map((p) => (
                      <li key={p.id}>
                        <label className="flex items-center gap-2 rounded px-1.5 py-0.5 text-[12px] hover:bg-paper-subtle">
                          <input
                            type="checkbox"
                            checked={exportSelector.pdfs.has(p.id)}
                            onChange={(e) => {
                              setExportSelector((prev) => {
                                if (!prev) return prev;
                                const next = new Set(prev.pdfs);
                                if (e.target.checked) next.add(p.id);
                                else next.delete(p.id);
                                return { ...prev, pdfs: next };
                              });
                            }}
                          />
                          <span className="truncate">{p.titre_pv || p.filename_original}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-[0.3px] text-text-tertiary">
                    Pièces client ({piecesClient.length})
                  </p>
                  <ul className="max-h-40 overflow-y-auto space-y-0.5">
                    {piecesClient.map((p) => (
                      <li key={p.id}>
                        <label className="flex items-center gap-2 rounded px-1.5 py-0.5 text-[12px] hover:bg-paper-subtle">
                          <input
                            type="checkbox"
                            checked={exportSelector.pcs.has(p.id)}
                            onChange={(e) => {
                              setExportSelector((prev) => {
                                if (!prev) return prev;
                                const next = new Set(prev.pcs);
                                if (e.target.checked) next.add(p.id);
                                else next.delete(p.id);
                                return { ...prev, pcs: next };
                              });
                            }}
                          />
                          <span className="truncate">
                            {p.titre}
                            {p.date_piece ? ` · ${p.date_piece}` : ''}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {exportStatus && <p className="text-xs text-text-tertiary">{exportStatus}</p>}

          <FileDropZone onFiles={onFilesSelected} disabled={stillUploading} />
          {uploads.length > 0 && (
            <ul className="divide-y divide-rule rounded-md border border-rule bg-paper-elevated text-xs">
              {uploads.map((u, idx) => (
                <li
                  key={`${u.name}-${idx}`}
                  className="flex items-center justify-between px-3 py-2"
                >
                  <span className="truncate font-mono text-text-secondary">{u.name}</span>
                  <span
                    className={
                      u.state === 'done'
                        ? 'text-cat-fav'
                        : u.state === 'failed'
                          ? 'text-cat-def'
                          : u.state === 'uploading'
                            ? 'text-text-primary'
                            : 'text-text-tertiary'
                    }
                  >
                    {u.state === 'pending' && 'en attente'}
                    {u.state === 'uploading' && 'envoi en cours…'}
                    {u.state === 'done' && '✓ ajouté'}
                    {u.state === 'failed' && `échec : ${u.detail ?? ''}`}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {pdfs.length === 0 ? (
            <p className="text-sm text-text-secondary">Aucune pièce dans ce dossier.</p>
          ) : (
            <div className="overflow-hidden rounded-md border border-rule bg-paper-elevated">
              <div className="flex items-center justify-between border-b border-rule bg-paper-card px-4 py-2.5 text-xs">
                <div>
                  <span className="font-medium uppercase tracking-[0.3px] text-text-primary">
                    Toutes les pièces
                  </span>
                  <span className="ml-1.5 font-normal text-text-tertiary">
                    · {pdfs.length} PDF{pdfs.length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              {pdfs.map((p) => {
                const annotsOnPdf = annotations.filter((a) => a.pdf_file_id === p.id).length;
                const treatedPages = pagesTreatedByPdf.get(p.id)?.size ?? 0;
                const totalPages = p.page_count ?? 0;
                const piecePct = totalPages > 0 ? Math.round((treatedPages / totalPages) * 100) : 0;
                return (
                  <Link
                    key={p.id}
                    to={`/dossiers/${id}/pieces/${p.id}`}
                    className="group grid items-center gap-3 border-b border-rule-soft px-4 py-2.5 text-[13px] no-underline last:border-b-0 hover:bg-paper-card"
                    style={{ gridTemplateColumns: '28px 1fr 180px 130px 60px 80px' }}
                  >
                    <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-cat-def-pastel font-serif text-[10px] font-semibold text-cat-def">
                      P
                    </span>
                    <div className="flex items-baseline gap-2 text-text-primary min-w-0">
                      <span className="truncate">{p.titre_pv || p.filename_original}</span>
                      {p.numero_pv && (
                        <span className="font-mono text-[11px] text-text-tertiary shrink-0">
                          PV n° {p.numero_pv}
                        </span>
                      )}
                      {annotsOnPdf > 0 && (
                        <span className="ml-1 text-[11px] font-medium text-accent-text shrink-0">
                          · {annotsOnPdf} annotation{annotsOnPdf > 1 ? 's' : ''}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void renamePdf(p.id, p.filename_original);
                        }}
                        className="ml-1 shrink-0 rounded p-0.5 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-paper-subtle hover:text-text-primary"
                        title="Renommer la pièce"
                      >
                        ✏
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setMigratePdf({ id: p.id, title: p.filename_original });
                        }}
                        className="shrink-0 rounded border border-accent/30 bg-accent-light px-1.5 py-0.5 text-[11px] text-accent-text transition hover:bg-accent hover:text-paper-elevated"
                        title="Ranger dans un autre bucket (Pièces client / Adverses / Conclusions / Autres)"
                      >
                        ↗ Ranger
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void removePdf(p.id, p.filename_original);
                        }}
                        className="shrink-0 rounded p-0.5 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-cat-def-pastel hover:text-cat-def"
                        title="Supprimer la pièce"
                      >
                        🗑
                      </button>
                    </div>
                    <span
                      className="font-mono text-[11px] tracking-[0.3px] text-text-secondary truncate flex items-center gap-1"
                      onClick={(e) => {
                        // Avoid navigating to piece editor when interacting
                        // with the inventaire <select>.
                        if ((e.target as HTMLElement).tagName === 'SELECT') {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      {inventaireEntries.length === 0 ? (
                        <span className="italic">— non coté —</span>
                      ) : (
                        <select
                          value={p.inventaire_entry_id ?? ''}
                          onChange={(e) => {
                            void linkPdfToEntry(p.id, e.target.value || null);
                          }}
                          onClick={(e) => e.preventDefault()}
                          className="w-full bg-transparent font-mono text-[11px] tracking-[0.3px] text-text-secondary truncate hover:text-text-primary focus:outline-none"
                          title="Lier cette pièce à une entrée d'inventaire"
                        >
                          <option value="">— non coté —</option>
                          {inventaireEntries.map((entry) => {
                            const cot = cotationLongPdf(entry);
                            const label =
                              cot === '— non coté'
                                ? entry.titre_piece || '(entrée sans cotation)'
                                : entry.titre_piece
                                  ? `${cot} · ${entry.titre_piece}`
                                  : cot;
                            return (
                              <option key={entry.id} value={entry.id}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <div
                        className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-paper-subtle"
                        role="progressbar"
                        aria-valuenow={piecePct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Progression ${treatedPages}/${totalPages || '?'} pages`}
                      >
                        <div
                          className={`h-full rounded-full transition-[width] ${
                            piecePct === 100 ? 'bg-cat-fav' : piecePct > 0 ? 'bg-accent' : ''
                          }`}
                          style={{ width: `${piecePct}%` }}
                        />
                      </div>
                      <span className="font-mono text-[10px] tracking-[0.3px] text-text-tertiary w-12 text-right">
                        {totalPages > 0 ? `${treatedPages}/${totalPages}` : '—'}
                      </span>
                    </div>
                    <span className="text-right font-mono text-[11px] text-text-tertiary">
                      {p.page_count != null ? `${p.page_count} p` : '—'}
                    </span>
                    <span
                      className={`inline-flex items-center justify-center rounded-pill px-2 py-[2px] text-[10px] font-medium ${
                        p.ocr_status === 'done'
                          ? 'bg-cat-fav-pastel text-cat-fav'
                          : p.ocr_status === 'processing'
                            ? 'bg-cat-imp-pastel text-cat-imp-text'
                            : 'bg-paper-subtle text-text-secondary'
                      }`}
                    >
                      OCR{' '}
                      {p.ocr_status === 'done'
                        ? 'ok'
                        : p.ocr_status === 'processing'
                          ? 'en cours'
                          : p.ocr_status}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
